import React, { useEffect, useRef, useState } from "react";
import { Download, X, Share2, Smartphone, MonitorSmartphone } from "lucide-react";

/**
 * InstallOfficeAppButton
 *
 * A one-tap install trigger for the "Mamamina Office" PWA. Handles three
 * flows because browsers behave differently:
 *   • Chrome / Edge / Brave — captures `beforeinstallprompt` and fires it
 *   • Safari iOS — no programmatic install; we show the manual "Add to
 *     Home Screen" walkthrough with the exact tap sequence
 *   • Firefox / everything else — same fallback modal, generic copy
 *
 * The button hides itself once the PWA is already installed (detected via
 * the `standalone` display mode media query).
 */
export default function InstallOfficeAppButton() {
  const deferredPromptRef = useRef(null);
  const [canPrompt, setCanPrompt] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [alreadyInstalled, setAlreadyInstalled] = useState(false);

  useEffect(() => {
    // Detect if already running as an installed PWA
    if (window.matchMedia("(display-mode: standalone)").matches ||
        window.navigator.standalone === true) {
      setAlreadyInstalled(true);
      return;
    }
    const onPrompt = (e) => {
      e.preventDefault();
      deferredPromptRef.current = e;
      setCanPrompt(true);
    };
    const onInstalled = () => setAlreadyInstalled(true);
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = async () => {
    const evt = deferredPromptRef.current;
    if (!evt) {
      setShowHelp(true);
      return;
    }
    evt.prompt();
    const choice = await evt.userChoice;
    if (choice.outcome === "accepted") {
      setAlreadyInstalled(true);
    }
    deferredPromptRef.current = null;
    setCanPrompt(false);
  };

  if (alreadyInstalled) return null;

  const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);

  return (
    <>
      <button
        onClick={install}
        data-testid="install-office-app-btn"
        className="inline-flex items-center gap-2 border border-[var(--mm-accent)] bg-[var(--mm-accent)]/10 text-[var(--mm-accent)] px-3 py-1.5 text-[10px] tracking-[0.22em] uppercase font-semibold hover:bg-[var(--mm-accent)] hover:text-[var(--mm-bg)] transition-all"
        title="Install Mamamina Office as a standalone app"
      >
        <Download size={12} />
        {canPrompt ? "Install Office App" : "Install to Home Screen"}
      </button>

      {showHelp && (
        <InstallHelpModal
          isIOS={isIOS}
          onClose={() => setShowHelp(false)}
        />
      )}
    </>
  );
}

function InstallHelpModal({ isIOS, onClose }) {
  return (
    <div
      className="fixed inset-0 z-[200] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="install-help-modal"
      onClick={onClose}
    >
      <div
        className="bg-[var(--mm-bg-alt)] border border-[var(--mm-accent)]/40 max-w-md w-full p-6 shadow-2xl relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-[var(--mm-cream-muted)] hover:text-[var(--mm-cream)]"
          aria-label="Close"
          data-testid="install-help-close"
        >
          <X size={18} />
        </button>

        <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--mm-accent)] mb-2">
          {isIOS ? "iOS · Safari" : "Add to Home Screen"}
        </div>
        <h3 className="font-display italic text-3xl text-[var(--mm-cream)] mb-4 leading-tight">
          Install Office in 3 taps
        </h3>

        {isIOS ? (
          <ol className="space-y-3 text-sm text-[var(--mm-cream)] leading-relaxed">
            <li className="flex gap-3">
              <span className="w-6 h-6 rounded-full bg-[var(--mm-accent)] text-[var(--mm-bg)] text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">1</span>
              <div>
                <div className="font-semibold">Tap the Share button</div>
                <div className="text-xs text-[var(--mm-cream-muted)] flex items-center gap-1 mt-0.5">
                  <Share2 size={12} /> · at the bottom of Safari (square with arrow)
                </div>
              </div>
            </li>
            <li className="flex gap-3">
              <span className="w-6 h-6 rounded-full bg-[var(--mm-accent)] text-[var(--mm-bg)] text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">2</span>
              <div>
                <div className="font-semibold">Scroll down · "Add to Home Screen"</div>
                <div className="text-xs text-[var(--mm-cream-muted)] mt-0.5">You'll see the gold Mamamina M badge.</div>
              </div>
            </li>
            <li className="flex gap-3">
              <span className="w-6 h-6 rounded-full bg-[var(--mm-accent)] text-[var(--mm-bg)] text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">3</span>
              <div>
                <div className="font-semibold">Tap "Add" · top-right</div>
                <div className="text-xs text-[var(--mm-cream-muted)] mt-0.5">The Office icon lands on your home screen.</div>
              </div>
            </li>
          </ol>
        ) : (
          <ol className="space-y-3 text-sm text-[var(--mm-cream)] leading-relaxed">
            <li className="flex gap-3">
              <span className="w-6 h-6 rounded-full bg-[var(--mm-accent)] text-[var(--mm-bg)] text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">1</span>
              <div>
                <div className="font-semibold">Open the browser menu</div>
                <div className="text-xs text-[var(--mm-cream-muted)] flex items-center gap-1 mt-0.5">
                  <MonitorSmartphone size={12} /> · three dots · top-right corner
                </div>
              </div>
            </li>
            <li className="flex gap-3">
              <span className="w-6 h-6 rounded-full bg-[var(--mm-accent)] text-[var(--mm-bg)] text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">2</span>
              <div>
                <div className="font-semibold">Tap "Install app" or "Add to Home Screen"</div>
                <div className="text-xs text-[var(--mm-cream-muted)] mt-0.5">The label depends on your browser — Chrome, Edge, Firefox all support it.</div>
              </div>
            </li>
            <li className="flex gap-3">
              <span className="w-6 h-6 rounded-full bg-[var(--mm-accent)] text-[var(--mm-bg)] text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">3</span>
              <div>
                <div className="font-semibold">Confirm install</div>
                <div className="text-xs text-[var(--mm-cream-muted)] mt-0.5">"Mamamina Office" appears in your Start Menu / Apps folder.</div>
              </div>
            </li>
          </ol>
        )}

        <div className="mt-6 pt-4 border-t border-[var(--mm-line)] flex items-center gap-3 text-xs text-[var(--mm-cream-muted)]">
          <Smartphone size={14} className="text-[var(--mm-accent)]" />
          <span>Runs full-screen · no browser chrome · offline-ready · dedicated app icon</span>
        </div>
      </div>
    </div>
  );
}
